import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { storyPromptCategories } from "./prompts/storyPrompts";
import { Baby, School, GraduationCap, Heart, Trophy, Plane, Lightbulb, Star, FileText } from "lucide-react";

interface StoryCategoriesViewProps {
  memorialId: string;
  onCategorySelect: (categoryId: string, categoryName: string) => void;
}

export const StoryCategoriesView = React.memo<StoryCategoriesViewProps>(({ 
  memorialId, 
  onCategorySelect 
}) => {
  const [storyCounts, setStoryCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);

  const memoizedMemorialId = useMemo(() => memorialId, [memorialId]);

  useEffect(() => {
    const fetchStoryCounts = async () => {
      try {
        const { data, error } = await supabase
          .from('stories')
          .select('category')
          .eq('memorial_id', memoizedMemorialId);

        if (error) throw error;

        const counts: Record<string, number> = {};
        
        data.forEach(story => {
          const category = story.category || 'other';
          counts[category] = (counts[category] || 0) + 1;
        });

        setStoryCounts(counts);
      } catch (error) {
        console.error('Error fetching story counts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoryCounts();
  }, [memoizedMemorialId]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-12 w-12 bg-gray-200 rounded-full mb-4" />
              <div className="h-4 bg-gray-200 rounded mb-2" />
              <div className="h-3 bg-gray-200 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Icon mapping
  const iconMap = {
    childhood: Baby,
    'making-life': School,
    family: Heart,
    achievements: Trophy,
    adventures: Plane,
    wisdom: Lightbulb,
    legacy: Star,
    other: FileText
  };

  // Only show categories that have stories
  const categoriesWithStories = storyPromptCategories.filter(category => 
    storyCounts[category.id] > 0
  );

  if (categoriesWithStories.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <h3 className="text-lg font-medium text-brand-text-primary mb-2">
            No stories shared yet
          </h3>
          <p className="text-brand-secondary">
            Life stories will appear here once they are added.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {categoriesWithStories.map((category) => {
        const Icon = iconMap[category.id as keyof typeof iconMap] || FileText;
        const count = storyCounts[category.id] || 0;
        
        return (
          <Card 
            key={category.id}
            className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105 border-2 hover:border-brand-primary/20"
            onClick={() => onCategorySelect(category.id, category.name)}
          >
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-brand-primary/10 rounded-full flex items-center justify-center">
                <Icon className="h-8 w-8 text-brand-primary" />
              </div>
              <h3 className="text-lg font-semibold text-brand-text-primary mb-2">
                {category.name}
              </h3>
              <p className="text-sm text-brand-secondary mb-3 line-clamp-2">
                {category.description}
              </p>
              <div className="text-xs text-brand-primary font-medium">
                {count} {count === 1 ? 'story' : 'stories'}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
});

StoryCategoriesView.displayName = 'StoryCategoriesView';